<header class="flex header p-2">
  <h2>{{ 'COA_NewAccount_Title' | translateWF: 'New account' }}</h2>
  <p-button icon="pi pi-times" variant="text" (click)="onClose()" />
</header>

<section class="content">
  <form [formGroup]="form" class="grid-2">
    <!-- Parent -->
    <div class="form-box span-2">
      <label ariaRequired="true">{{ 'COA_NewAccount_Parent' | translateWF: 'Parent account' }}</label>
      <p-select
        formControlName="parentId"
        [options]="parentOptions()"
        optionLabel="label"
        optionValue="value"
        [filter]="true"
        filterBy="label"
        appendTo="body"
        styleClass="w-full"
        [placeholder]="'COA_NewAccount_SelectParent' | translateWF: 'Select a parent account'"
      />
      <small class="text-surface-500">{{ 'COA_NewAccount_Parent_Hint' | translateWF: 'The new account is created one level below this account.' }}</small>
    </div>

    <!-- Account number — continues the parent's numbering; only typed when there is no sibling to learn from -->
    <div class="form-box">
      <label ariaRequired="true">{{ 'COA_NewAccount_Number' | translateWF: 'Account number' }}</label>
      <input pInputText formControlName="accountNumber" class="w-full" [readonly]="true" />
      @if (numberUnavailable()) {
        <small class="text-red-500">
          {{ 'COA_NewAccount_Number_Unavailable' | translateWF: 'No number is available under this parent — its number leaves no digits for a sub-level.' }}
        </small>
      } @else if (selectedParent()) {
        <small class="text-surface-500"> {{ 'COA_NewAccount_Number_Derived' | translateWF: 'Next available number under' }} {{ selectedParent()?.accountNumber }} </small>
      }
    </div>

    <!-- Account type — the parent's, always. NormalBalance derives from it and the tree rolls balances up
         blindly, so a child of a different type would corrupt the parent's total. -->
    <div class="form-box">
      <label>{{ 'COA_NewAccount_Type' | translateWF: 'Account type' }}</label>
      <input pInputText [value]="parentTypeLabel()" class="w-full" [disabled]="true" />
      <small class="text-surface-500">{{ 'COA_NewAccount_Type_Hint' | translateWF: 'Inherited from the parent account.' }}</small>
    </div>

    <!-- Name EN -->
    <div class="form-box">
      <label ariaRequired="true">{{ 'COA_NewAccount_NameEn' | translateWF: 'Name (English)' }}</label>
      <input pInputText formControlName="nameEn" class="w-full" />
    </div>

    <!-- Name ES -->
    <div class="form-box">
      <label>{{ 'COA_NewAccount_NameEs' | translateWF: 'Name (Spanish)' }}</label>
      <input pInputText formControlName="nameEs" class="w-full" [placeholder]="'COA_NewAccount_NameEs_Hint' | translateWF: 'Defaults to the English name'" />
    </div>

    <!-- Currency -->
    <div class="form-box">
      <label ariaRequired="true">{{ 'COA_NewAccount_Currency' | translateWF: 'Currency' }}</label>
      <p-select formControlName="currency" [options]="currencyOptions" optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full" />
      <small class="text-surface-500">
        {{
          'COA_NewAccount_Currency_Hint'
            | translateWF: 'Defaults to the parent, but can differ — a USD bank account belongs under a HNL bank group. Cannot be changed once the account exists.'
        }}
      </small>
    </div>

    <!-- Subledger type -->
    <div class="form-box">
      <label>{{ 'COA_NewAccount_Subledger' | translateWF: 'Subledger type' }}</label>
      <p-select formControlName="subledgerType" [options]="subledgerOptions" optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full" />
    </div>

    <!-- Authorized posting modules — for every account, not just subledger ones: this is what decides
         which screens may post here, and leaving it empty is what keeps an ordinary account open to all. -->
    <div class="form-box span-2">
      <label [attr.ariaRequired]="isSubledger() ? true : null">{{ 'COA_NewAccount_PostingModules' | translateWF: 'Who may post here' }}</label>
      <p-multiSelect
        formControlName="postingModules"
        [options]="postingModuleOptions"
        optionLabel="label"
        optionValue="value"
        appendTo="body"
        styleClass="w-full"
        [placeholder]="'COA_NewAccount_PostingModules_Ph' | translateWF: 'Any module (leave empty)'"
      />
      @if (isSubledger()) {
        <small class="text-surface-500">{{ 'COA_NewAccount_PostingModules_Sub' | translateWF: 'A subledger account must authorize at least one module.' }}</small>
      } @else {
        <small class="text-surface-500">
          {{
            'COA_NewAccount_PostingModules_Hint' | translateWF: 'Leave empty to allow any module. Pick modules to reserve this account to them — other screens will grey it out.'
          }}
        </small>
      }
    </div>
  </form>
</section>

<footer>
  <p-button [label]="'Cancel' | translateWF: 'Cancel'" variant="outlined" (click)="onClose()" />
  <p-button [label]="'Create' | translateWF: 'Create'" [disabled]="form.invalid" (click)="onSave()" />
</footer>
